import Dialog from 'components/Dialog';
import React from 'react';
import {Grid} from '@mui/material';
import FormikTextField from 'components/inputs/FormikTextField';
import {FormFooterButtons} from 'components/buttons/FormFooterButtons';
import * as yup from 'common/yup';
import {usePostApi} from 'common/hooks/usePostApi';
import api from 'api';
import {INav} from 'features/layout/redux/helpers/FoldersHelper';
import {useDispatch, useSelector} from 'react-redux';
import {layoutActions} from 'features/layout/redux';
import {unpackId} from 'common/diplomatIdUtil';
import {DiplomatObjectType} from 'types/DiplomatObjectType';
import {Trans, useTranslation} from 'react-i18next';
import selectors from 'features/layout/redux/selectors';
import {IDiplomatObject} from 'types/DiplomatObject';
import {action} from 'redux/helpers';
import {pageTypes} from 'features/layout/CommonPage/redux';
import Formik from 'components/hook-form/Formik';

const validationSchema = yup.object({
   keyName: yup.string().required(),
});

type RenameKeyDialogProps = {
   data?: INav;
   onClose: () => void;
};

const RenameKeyDialog = ({data, onClose}: RenameKeyDialogProps) => {
   const {t} = useTranslation();
   const dispatch = useDispatch();
   const {ssl, ssh, openpgp} = useSelector(selectors.keysSelector);
   const keysForValidation = React.useRef<Record<string, IDiplomatObject[]>>(openpgp);
   const onSuccess = (data: IDiplomatObject) => {
      dispatch(layoutActions.loadTreeNav());
      dispatch(action(pageTypes.GET_DATA.SUCCESS, data));
      onClose();
   };
   const {id} = unpackId(data?.id);
   const {request} = usePostApi(`${api.keys}/${id}/rename`, {onSuccess, onFailure: onClose});
   const keyName = React.useMemo(() => {
      switch (data?.objectType) {
         case DiplomatObjectType.enum.SSL_CERTIFICATE:
         case DiplomatObjectType.enum.SSL_CLIENT_CERTIFICATE:
            keysForValidation.current = ssl;
            return t('SSL Certificate');
         case DiplomatObjectType.enum.SSH_HOST_KEY:
         case DiplomatObjectType.enum.SSH_KEY:
            keysForValidation.current = ssh;
            return t('SSH Key');
         default:
            return t('Key');
      }
   }, [data, ssh, ssl, t]);

   const validateName = (value: string) => {
      const isNameExists = Object.values(
         keysForValidation.current
      ).some((keys: IDiplomatObject[]) => keys.some((key: IDiplomatObject) => key.name === value));
      if (isNameExists) {
         return t(`Diplomat already contains a {{keyName}} named {{name}}`, {keyName, name: value});
      }
   };

   return (
      <Dialog maxWidth="sm" header={<Trans t={t}>Rename {{keyName}}</Trans>} close={onClose}>
         <Formik
            initialValues={validationSchema.cast({keyName: data?.name})}
            onSubmit={(values, formikHelpers) => {
               request(null, {
                  searchParams: {...values, keyType: String(data?.objectType)},
                  helpers: formikHelpers,
               });
            }}
         >
            <>
               <Grid container spacing={2} justifyContent="center">
                  <Grid item xs={8}>
                     <FormikTextField
                        autoFocus
                        type="text"
                        name="keyName"
                        label={t('New {{keyName}} Name:', {keyName})}
                        required
                        validate={validateName}
                     />
                  </Grid>
               </Grid>
               <Grid item xs={12}>
                  <FormFooterButtons cancel={onClose} />
               </Grid>
            </>
         </Formik>
      </Dialog>
   );
};

export default RenameKeyDialog;
